import React from 'react';
import { Box, Chip, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { CapacityStats } from '../utils/capacityCalculationUtils';
import { CalendarView } from '../calendarWrapper/CalendarWrapper';
import { useAuth } from '../../session/InternalAuthProvider';
import { AccountData } from '../../api';

interface CapacityStatsGridProps {
  capacityStats: CapacityStats;
  calendarView: CalendarView;
  currentDate: string;
}

const CapacityStatsGrid: React.FC<CapacityStatsGridProps> = ({ capacityStats, calendarView, currentDate }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const schedulerConfigs = account?.shared_resources?.scheduler_configs || [];
  const shouldShoHoursCalculations = schedulerConfigs.length > 0;
  // Helper function to format minutes to hours
  const formatMinutesToHours = (minutes: number): string => {
    const totalHours = minutes / 60;
    // Round to nearest 0.5
    const roundedHours = Math.ceil(totalHours * 2) / 2;
    return `${roundedHours}h`;
  };

  // Mint by default; the same warning signal as before, in tokens: clay from 70%, red from 90%.
  const getUsageColor = (percentage: number): { bg: string; fg: string } => {
    if (percentage >= 90) return { bg: theme.palette.error.light, fg: theme.palette.error.main };
    if (percentage >= 70) return { bg: theme.palette.mb.claySoft, fg: theme.palette.mb.clayInk };
    return { bg: theme.palette.primary.light, fg: theme.palette.primary.main };
  };

  // Get the appropriate stats based on calendar view
  const getStatsForCurrentView = () => {
    switch (calendarView) {
      case CalendarView.DAY: {
        // Show only today's data for day view
        if (capacityStats.daily.length > 0) {
          const todayStats = capacityStats.daily[0]; // First day should be today
          return {
            period: t('capacity.today'),
            usedMinutes: todayStats.usedMinutes,
            totalWorkingMinutes: todayStats.totalWorkingMinutes,
            orderCount: todayStats.orderCount,
            eventCount: todayStats.eventCount,
            usagePercentage: todayStats.usagePercentage,
            personCount: todayStats.personCount || 0,
          };
        }
        break;
      }

      case CalendarView.THREE_DAY: {
        // Show aggregated stats for the visible 3-day period
        const visibleDays = capacityStats.daily.slice(0, 3); // Only first 3 days
        const dailyTotal = visibleDays.reduce(
          (acc, day) => ({
            usedMinutes: acc.usedMinutes + day.usedMinutes,
            totalWorkingMinutes: acc.totalWorkingMinutes + day.totalWorkingMinutes,
            orderCount: acc.orderCount + day.orderCount,
            eventCount: acc.eventCount + day.eventCount,
            personCount: acc.personCount + (day.personCount || 0),
          }),
          { usedMinutes: 0, totalWorkingMinutes: 0, orderCount: 0, eventCount: 0, personCount: 0 },
        );
        const dailyUsagePercentage =
          dailyTotal.totalWorkingMinutes > 0 ? (dailyTotal.usedMinutes / dailyTotal.totalWorkingMinutes) * 100 : 0;

        return {
          period: `${visibleDays.length} ${t('capacity.days')}`,
          ...dailyTotal,
          usagePercentage: dailyUsagePercentage,
        };
      }

      case CalendarView.WEEK: {
        // Show current week stats
        if (capacityStats.weekly.length > 0) {
          // Find the week that contains the current date
          const currentWeekStart = moment(currentDate).clone().startOf('week').format('YYYY-MM-DD');
          const weekStats =
            capacityStats.weekly.find((week) => week.period === currentWeekStart) || capacityStats.weekly[0];

          return {
            period: t('capacity.thisWeek'),
            usedMinutes: weekStats.usedMinutes,
            totalWorkingMinutes: weekStats.totalWorkingMinutes,
            orderCount: weekStats.orderCount,
            eventCount: weekStats.eventCount,
            usagePercentage: weekStats.usagePercentage,
            personCount: weekStats.personCount || 0,
          };
        }
        break;
      }

      case CalendarView.MONTH: {
        // Show current month stats
        if (capacityStats.monthly.length > 0) {
          // Find the month that contains the current date
          const currentMonth = moment(currentDate).format('YYYY-MM');
          const monthStats =
            capacityStats.monthly.find((month) => month.period === currentMonth) || capacityStats.monthly[0];

          return {
            period: t('capacity.thisMonth'),
            usedMinutes: monthStats.usedMinutes,
            totalWorkingMinutes: monthStats.totalWorkingMinutes,
            orderCount: monthStats.orderCount,
            eventCount: monthStats.eventCount,
            usagePercentage: monthStats.usagePercentage,
            personCount: monthStats.personCount || 0,
          };
        }
        break;
      }

      case CalendarView.CONDENSED:
      default: {
        // Show aggregated stats for all visible data
        const allTotal = capacityStats.daily.reduce(
          (acc, day) => ({
            usedMinutes: acc.usedMinutes + day.usedMinutes,
            totalWorkingMinutes: acc.totalWorkingMinutes + day.totalWorkingMinutes,
            orderCount: acc.orderCount + day.orderCount,
            eventCount: acc.eventCount + day.eventCount,
            personCount: acc.personCount + day.personCount,
          }),
          { usedMinutes: 0, totalWorkingMinutes: 0, orderCount: 0, eventCount: 0, personCount: 0 },
        );
        const allUsagePercentage =
          allTotal.totalWorkingMinutes > 0 ? (allTotal.usedMinutes / allTotal.totalWorkingMinutes) * 100 : 0;

        return {
          period: t('capacity.overview'),
          ...allTotal,
          usagePercentage: allUsagePercentage,
        };
      }
    }

    return null;
  };

  const stats = getStatsForCurrentView();
  const usageColor = stats ? getUsageColor(stats.usagePercentage) : null;

  // Don't render if no data
  if (!stats || stats.totalWorkingMinutes === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        px: 2.25,
        py: 1.75,
        width: 'fit-content',
        backgroundColor: 'background.paper',
      }}>
      <Typography variant="body2" fontSize={14} fontWeight="medium" color="text.secondary">
        {stats.period}
      </Typography>
      {shouldShoHoursCalculations && usageColor && (
        <Chip
          label={`${stats.usagePercentage.toFixed(1)}%`}
          size="small"
          dir="ltr"
          sx={{
            bgcolor: usageColor.bg,
            color: usageColor.fg,
            fontWeight: 600,
            borderRadius: '4px',
            minWidth: 0,
            fontVariantNumeric: 'tabular-nums',
          }}
        />
      )}
      {shouldShoHoursCalculations ? (
        <Typography variant="body2" fontSize={14} color="text.secondary" dir="ltr">
          {formatMinutesToHours(stats.usedMinutes)} / {formatMinutesToHours(stats.totalWorkingMinutes)}
        </Typography>
      ) : (
        <Typography variant="body2" fontSize={14} color="text.secondary" dir="ltr">
          {formatMinutesToHours(stats.usedMinutes)}
        </Typography>
      )}

      <Box sx={{ display: 'flex', gap: 1 }}>
        {stats.orderCount > 0 && (
          <Typography variant="body2" fontSize={14} color="text.secondary">
            {stats.orderCount} {t('capacity.orders')}
          </Typography>
        )}
        {stats.eventCount > 0 && (
          <Typography variant="body2" fontSize={14} color="text.secondary">
            {stats.eventCount} {t('capacity.events')}
          </Typography>
        )}
        <Typography variant="body2" fontSize={14} color="text.secondary">
          {stats.personCount} {t('capacity.persons')}
        </Typography>
      </Box>
    </Box>
  );
};

export default CapacityStatsGrid;
